/**
 * UserFormDialog - Dialog component for creating and editing users.
 *
 * Displays a modal form for user management operations.
 * Uses react-hook-form with Zod validation for form state management.
 * Validates form data and calls onSave with validated data when form is submitted.
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { Button } from '@lean-agility/design-system/react';
import { Input } from '@lean-agility/design-system/react';
import { Checkbox } from '@/components/atoms';
import { Dialog, DialogActions } from '@/components/dialog';
import {
  createUserSchema,
  updateUserSchema,
  type CreateUserFormValues,
  type UpdateUserFormValues,
  type CreateUserFormData,
  type UpdateUserFormData,
} from '../../validation/userFormSchemas';
import type { UserResponse } from '@/lib/api';

export interface UserFormDialogProps {
  /**
   * User to edit, or null to create a new user.
   */
  user: UserResponse | null;
  /**
   * Callback when dialog is closed.
   */
  onClose: () => void;
  /**
   * Callback invoked when form is submitted with valid data.
   * Receives the validated form data.
   */
  onSave: (data: CreateUserFormData | UpdateUserFormData) => Promise<void> | void;
  /**
   * Whether the form is in a loading/submitting state.
   * @default false
   */
  isLoading?: boolean;
}

export function UserFormDialog({ user, onClose, onSave, isLoading = false }: UserFormDialogProps) {
  const isEditing = !!user;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateUserFormValues | UpdateUserFormValues, unknown, CreateUserFormData | UpdateUserFormData>({
    resolver: zodResolver(isEditing ? updateUserSchema : createUserSchema),
    defaultValues: {
      email: '',
      password: '',
      name: '',
      is_admin: false,
      expiry_date: '',
    },
  });

  // Reset form when user prop changes
  useEffect(() => {
    if (user) {
      reset({
        email: user.email,
        password: '',
        name: user.name || '',
        is_admin: user.is_admin,
        expiry_date: user.expiry_date ? new Date(user.expiry_date).toISOString().split('T')[0] || '' : '',
      });
    } else {
      reset({
        email: '',
        password: '',
        name: '',
        is_admin: false,
        expiry_date: '',
      });
    }
  }, [user, reset]);

  const isFormLoading = isLoading || isSubmitting;

  return (
    <Dialog title={isEditing ? 'Edit User' : 'Create User'} onClose={onClose} dismissible={!isFormLoading}>
      <form onSubmit={handleSubmit(onSave)} className="space-y-4" noValidate>
        <Input
          {...register('email')}
          type="email"
          label="Email"
          autoComplete="email"
          error={errors.email?.message}
          disabled={isEditing || isFormLoading}
          autoFocus
        />
        <Input
          {...register('password')}
          type="password"
          label={isEditing ? 'New Password (leave empty to keep current)' : 'Password'}
          autoComplete={isEditing ? 'new-password' : 'new-password'}
          error={errors.password?.message}
          hint={isEditing ? 'Leave empty to keep the current password' : undefined}
          disabled={isFormLoading}
        />
        <Input
          {...register('name')}
          type="text"
          label="Name (optional)"
          placeholder="Display name"
          autoComplete="name"
          error={errors.name?.message}
          disabled={isFormLoading}
        />
        <Checkbox {...register('is_admin')} label="Administrator" error={errors.is_admin?.message} />
        <Input
          {...register('expiry_date')}
          type="date"
          label="Expiry Date (optional)"
          hint="Account will expire on this date"
          error={errors.expiry_date?.message}
          disabled={isFormLoading}
        />
        <DialogActions>
          <Button variant="ghost" type="button" onClick={onClose} disabled={isFormLoading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isFormLoading}>
            {isSubmitting ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isEditing ? 'Updating...' : 'Creating...'}
              </>
            ) : isEditing ? (
              'Update'
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
